import { useState } from "react"
import { useTranslation } from "react-i18next"
import * as Tabs from "@radix-ui/react-tabs"
import { Tab } from "../ui/Tab"
import clsx from "clsx"

import { Dialog, DialogProps } from "../ui/Dialog"
import { Button } from "../ui/Button"
import { SearchField } from "../ui/SearchField"
import { analytics } from "@/telemetry/amplitude"
import { isAcceptedFile, openFilesWithPicker } from "@/utils/file"
import toast from "react-hot-toast"
import imagesUrl from "@assets/edit_small.png?url"
import { canPopDialog, pushDialog } from "@/store/dialogs-state"
import { SelectCGCollectionView } from "./SelectCGCollectionDialog"
import { ProjectStyleTypes } from "@/store/style/style-state"
import { CollectionResponseDTO } from "@/api/PSApiOperations"
import { SelectSetup, SetupType } from "./SelectSetupDialog"
import { isAIDebugEnabled, isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { Switch } from "../ui/Switch"
import { useAtom, useAtomValue } from "jotai"
import { useLRParamsForJpegReferenceAtom } from "@/lib/ai/calibration/ai-calibration-feature"
import { uuid } from "@/utils/uuid"
import { StyleCreateLoadingDialog } from "./StyleCreateLoadingDialog"
import { NewStyleSetupOptions } from "@/store/project/create-project"
import { projectAtom } from "@/store/project/projects-state"
import { StyleDraft } from "@/store/style/style-state"

const STYLE_REFERENCE_PHOTO_LIMIT = 50
type CreateStyleTab = "client-gallery" | "images" | "edits"

export interface StyleDraftSelectOptions {
	styleDraft: StyleDraft
	projectStyleType: ProjectStyleTypes
}

export type CreateStyleSelectOptions = NewStyleSetupOptions | StyleDraftSelectOptions

export const CreateStyleDialog = (
	props: DialogProps & {
		onSelect: (options: CreateStyleSelectOptions) => void | Promise<void>
		initialCollection?: CollectionResponseDTO
	},
) => {
	const { t } = useTranslation("components")
	const [currentTab, setCurrentTab] = useState<CreateStyleTab>(
		props.initialCollection
			? "client-gallery"
			: isPixiesetAuthEnabled()
				? "client-gallery"
				: "images",
	)

	const [selectedCollection, setSelectedCollection] = useState<CollectionResponseDTO | undefined>(
		props.initialCollection,
	)

	const [selectedSetup, setSelectedSetup] = useState<SetupType>("express")
	const [gallerySearchTerm, setGallerySearchTerm] = useState("")
	const currentProject = useAtomValue(projectAtom)

	const continueButton = () => {
		if (currentTab == "images") {
			return null
		} else if (currentTab == "edits") {
			return (
				<div className="flex w-full flex-row items-center justify-end">
					<Button
						trackingLabel="select_setup_continue"
						aria-label={t("createStyleDialog.ariaContinueSetup")}
						variant="primary"
						onClick={() => {
							void props.onSelect({
								isExpressSetup: selectedSetup === "express",
							})
						}}
						disabled={!selectedSetup}
					>
						{t("common:action.continue")}
					</Button>
				</div>
			)
		} else {
			return (
				<div className="flex w-full flex-row items-center justify-end">
					<Button
						trackingLabel="select_collection_continue"
						aria-label={t("createStyleDialog.ariaContinueWithCollection")}
						variant="primary"
						onClick={() => {
							void handleSelectCollection(selectedCollection)
						}}
						disabled={!selectedCollection}
					>
						{t("common:action.continue")}
					</Button>
				</div>
			)
		}
	}

	const handleSelectImages = (files: File[]) => {
		if (files.length > 0) {
			const projectStyleType: ProjectStyleTypes = "Images"
			pushDialog(StyleCreateLoadingDialog, {
				...props,
				styleCreationId: uuid(),
				projectStyleType,
				onStyleSelected: props.onSelect,
				referenceFiles: files,
				trackingViewName: "style_create_loading",
			})
		}
	}

	const handleSelectCollection = (collection?: CollectionResponseDTO) => {
		if (!collection) {
			toast.error(t("createStyleDialog.invalidCollection"))
			return
		}

		if (collection.photo_count === 0) {
			toast.error(t("createStyleDialog.collectionEmpty"))
			return
		}

		const projectStyleType: ProjectStyleTypes = "Pixieset Gallery" // i18n-ignore: analytics value
		pushDialog(StyleCreateLoadingDialog, {
			...props,
			styleCreationId: uuid(),
			projectStyleType,
			onStyleSelected: props.onSelect,
			collection,
			trackingViewName: "style_create_loading",
		})
	}

	const selectImages = async () => {
		const pickerOptions: OpenFilePickerOptions = {
			types: [
				{
					description: "Image", // i18n-ignore — browser File Picker API
					accept: {
						"image/*": [".jpg", ".jpeg", ".png", ".webp", ".heic", ".avif"],
					},
				},
			],
			excludeAcceptAllOption: true,
			multiple: true,
		}

		try {
			const files = await openFilesWithPicker(pickerOptions)
			if (files.length === 0) return
			if (files.length > STYLE_REFERENCE_PHOTO_LIMIT) {
				toast.error(
					t("createStyleDialog.tooManyImages", { limit: STYLE_REFERENCE_PHOTO_LIMIT }),
				)
				return
			}
			handleSelectImages(files)
		} catch {
			analytics.trackEvent({
				label: "select_images_error",
				type: "error",
				value: "file_picker",
			})
		}
	}

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		setIsDragging(false)

		e.stopPropagation()
		e.preventDefault()

		const files = e.dataTransfer.files
		if (files.length > STYLE_REFERENCE_PHOTO_LIMIT) {
			toast.error(
				t("createStyleDialog.tooManyImages", { limit: STYLE_REFERENCE_PHOTO_LIMIT }),
			)
			return
		}

		const validFiles: File[] = []

		try {
			for (const file of files) {
				if (isAcceptedFile(file.name, ["jpg", "jpeg", "png", "webp", "heic", "avif"])) {
					validFiles.push(file)
				}
			}

			if (validFiles.length === 0) {
				toast.error(t("createStyleDialog.invalidFileFormat"))
			} else {
				console.log("validFiles", validFiles)
				void handleSelectImages(validFiles)
			}

			analytics.trackEvent({
				label: "select_images_success",
				type: "event",
				value: "drag_and_drop",
			})
		} catch (e: unknown) {
			console.error(e)
			analytics.trackEvent({
				label: "select_images_error",
				type: "error",
				value: "drag_and_drop",
			})
		}
	}

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(true)
		const item = e.dataTransfer.items[0]
		if (!item) {
			return
		}
	}

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
	}

	const [isDragging, setIsDragging] = useState(false)
	const [useLRParams, setUseLRParams] = useAtom(useLRParamsForJpegReferenceAtom)

	const renderSelectCollection = () => {
		return (
			<div className="flex size-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
				<div className="grid shrink-0 grid-cols-3 gap-3">
					<SearchField
						value={gallerySearchTerm}
						onChange={setGallerySearchTerm}
						placeholder={t("createStyleDialog.searchGalleries")}
						aria-label={t("createStyleDialog.searchGalleries")}
						className="w-full min-w-0"
						data-disable-enter-key="true"
					/>
				</div>
				<SelectCGCollectionView
					searchTerm={gallerySearchTerm}
					onSelect={(collection) => {
						setSelectedCollection(collection)
					}}
					selectedCollection={selectedCollection}
					preselectedCollection={props.initialCollection}
					mode="confirm"
				/>
			</div>
		)
	}

	const renderEditImages = () => {
		return (
			<div className="flex size-full flex-col">
				<SelectSetup onSelect={(setup) => setSelectedSetup(setup)} />
			</div>
		)
	}
	const renderSelectImages = () => {
		return (
			<div className="flex size-full min-h-0 flex-col space-y-4 overflow-y-auto">
				{isAIDebugEnabled() /* Debug Mode */ && (
					<>
						{/* Algorithm Version */}
						<div className="border-t border-base-700/50 pt-2">
							<div className="text-sm text-base-200">
								{t("createStyleDialog.algorithmVersion")}
							</div>
						</div>
						{/* Algorithm Options */}
						<div className="space-y-2 rounded-md bg-base-800/50 p-4 pt-0">
							<div className="flex items-center justify-between">
								<div className="text-sm text-base-200">
									{t("createStyleDialog.useEmbeddedParameters")}
								</div>
								<Switch
									trackingLabel="use_lr_params_toggle"
									aria-label={t("createStyleDialog.ariaToggleUseLightroom")}
									checked={useLRParams}
									onCheckedChange={setUseLRParams}
								/>
							</div>
						</div>
					</>
				)}

				<div
					className={clsx(
						isDragging ? "bg-base-400/30" : "bg-base-400/10",
						"flex w-full flex-1 rounded-md border border-dashed border-base-400",
					)}
				>
					<div className="flex w-full flex-col items-center justify-center space-y-2">
						<img className="w-1/5 self-center object-cover" src={imagesUrl} />

						<div className="text-xs">{t("createStyleDialog.dropImagesHint")}</div>
						<div className="flex flex-row items-center space-x-1 text-xs text-base-400">
							<div className="h-px w-10 bg-base-700"></div>
							<div>{t("common:label.or")}</div>
							<div className="h-px w-10 bg-base-700"></div>
						</div>
						<div className="pb-4">
							<Button
								trackingLabel="select_images"
								aria-label={t("createStyleDialog.ariaSelectReferenceImages")}
								size="sm"
								variant="primary"
								onClick={() => {
									void selectImages()
								}}
							>
								{t("createStyleDialog.selectImages")}
							</Button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	const getExplanationText = () => {
		switch (currentTab) {
			case "client-gallery":
				return t("createStyleDialog.explanationClientGallery")
			case "images":
				return t("createStyleDialog.explanationImages", {
					limit: STYLE_REFERENCE_PHOTO_LIMIT,
				})
			case "edits":
				return t("createStyleDialog.explanationEdits")
			default:
				return ""
		}
	}

	const renderTabs = () => {
		return (
			<Tabs.Root
				defaultValue={currentTab}
				className="flex min-h-0 flex-1 flex-col space-y-4 overflow-hidden"
				onValueChange={(tab) => setCurrentTab(tab as CreateStyleTab)}
			>
				<Tabs.List
					aria-label={t("createStyleDialog.ariaSelectStyleSource")}
					className="flex w-full shrink-0 flex-row space-x-0 overflow-hidden rounded-sm bg-base-700"
				>
					{isPixiesetAuthEnabled() && (
						<Tab value="client-gallery">
							{t("createStyleDialog.fromPixiesetGalleries")}
						</Tab>
					)}
					<Tab value="images">{t("createStyleDialog.fromUploads")}</Tab>
					{!!currentProject && (
						<Tab value="edits">{t("createStyleDialog.fromManualEdit")}</Tab>
					)}
				</Tabs.List>

				{isPixiesetAuthEnabled() && (
					<Tabs.Content
						className="flex h-full min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
						value="client-gallery"
					>
						{renderSelectCollection()}
					</Tabs.Content>
				)}

				<Tabs.Content
					className="flex h-full min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
					value="images"
				>
					{renderSelectImages()}
				</Tabs.Content>

				{!!currentProject && (
					<Tabs.Content
						className="flex h-full min-h-0 flex-1 flex-col overflow-hidden data-[state=inactive]:hidden"
						value="edits"
					>
						{renderEditImages()}
					</Tabs.Content>
				)}
			</Tabs.Root>
		)
	}
	return (
		<Dialog
			contentSize="lg"
			{...props}
			canGoBack={canPopDialog()}
			trackingViewName="select_images_dialog"
			bottomButtons={continueButton()}
		>
			<div
				className="flex min-h-0 flex-1 flex-col overflow-hidden"
				data-allow-file-drop
				onDragOver={handleDragOver}
				onDrop={handleDrop}
				onDragLeave={handleDragLeave}
			>
				<div className="text-start text-md font-bold">{t("createStyleDialog.title")}</div>
				<div className="mb-5 mt-2 text-start text-sm text-base-400">
					{getExplanationText()}
				</div>
				{renderTabs()}
			</div>
		</Dialog>
	)
}
